import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router';
import { animate, style, transition, trigger } from '@angular/animations';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MenuModule } from 'headlessui-angular';
import { FormsModule } from '@angular/forms';
import { NgClass, NgIf } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { IconModule } from '@crm-workspace/common-lib';
import { merge, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppService } from '../services/app.service';
import { AuthService } from '../core/services/auth/auth.service';
import { NotificationService } from '../core/services/notification/notification.service';
import { NotificationDropdownPanelComponent } from '../shared/components/notification-dropdown-panel/notification-dropdown-panel.component';
import { GlobalCommandPaletteService } from '../core/services/global-command-palette/global-command-palette.service';
import {
    mapProfileMeToSummary,
} from '../modules/settings/profile/profile-me.mapper';
import {
	initialsFromPersonName,
	isStaticProfileAvatarUrl,
} from '@app/shared/utils/display-name-initials.util';
import { isAdminRoleName } from '../core/services/workspace-member/workspace-member.util';
import { SettingsUsersService } from '../pages/settings/users/settings-users.service';
import { WorkspaceSwitcherComponent } from '../shared/components/workspace-switcher/workspace-switcher.component';
import { SearchInputComponent } from '../shared/components/ui/search-input/search-input.component';
import { ChangelogService } from '../modules/changelog/changelog.service';
@Component({
    selector: 'header',
    templateUrl: './header.html',
    animations: [
        trigger('toggleAnimation', [
            transition(':enter', [style({ opacity: 0, transform: 'scale(0.95)' }), animate('100ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))]),
            transition(':leave', [animate('75ms', style({ opacity: 0, transform: 'scale(0.95)' }))]),
        ]),
    ],
    standalone: true,
    imports: [
        NgClass,
        RouterLink,
        FormsModule,
        NgIf,
        MenuModule,
        TranslateModule,
        IconModule,
        NotificationDropdownPanelComponent,
        WorkspaceSwitcherComponent,
        SearchInputComponent,
    ],
})
export class HeaderComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);
    readonly paletteService = inject(GlobalCommandPaletteService);
    readonly notificationService = inject(NotificationService);
    readonly changelogService = inject(ChangelogService);
    @ViewChild('notificationBell') notificationBell?: ElementRef<HTMLElement>;
    @ViewChild('notificationPanel') notificationPanel?: ElementRef<HTMLElement>;
    store: any;
    activeDropdown: string[] = [];
    notificationPanelOpen = false;
    /** From {@link AuthService.getProfileMe} — shown in header pill + profile menu. */
    headerDisplayName = '';
    headerUserEmail = '';
    headerFirstName = '';
    headerLastName = '';
    headerAvatarUrl = '';
    /** Set when profile image fails to load — show letter avatar instead. */
    headerAvatarLoadFailed = false;
    isWorkspaceAdmin = false;
    messages = [
        {
            id: 1,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`
            ),
            title: 'Congratulations!',
            message: 'Your OS has been updated.',
            time: '1hr',
        },
        {
            id: 2,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`
            ),
            title: 'Did you know?',
            message: 'You can switch between artboards.',
            time: '2hr',
        },
        {
            id: 3,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`
            ),
            title: 'Something went wrong!',
            message: 'Send Reposrt',
            time: '2days',
        },
        {
            id: 4,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`
            ),
            title: 'Warning',
            message: 'Your password strength is low.',
            time: '5days',
        },
    ];

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
        private appSetting: AppService,
        private sanitizer: DomSanitizer,
        private dialogRef: MatDialog,
        private auth: AuthService,
        private membersApi: SettingsUsersService,
    ) {
        this.initStore();
    }
    async initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }

    ngOnInit(): void {
        merge(
            of(undefined).pipe(switchMap(() => this.auth.getProfileMe({ bustCache: true }))),
            this.auth.profileMePushed$,
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (me) => {
                    const s = mapProfileMeToSummary(me);
                    this.headerDisplayName = s.displayName;
                    this.headerUserEmail = s.email;
                    this.headerFirstName = (me.firstName ?? '').trim();
                    this.headerLastName = (me.lastName ?? '').trim();
                    this.headerAvatarUrl = s.photoUrl;
                    this.headerAvatarLoadFailed = false;
                    this.isWorkspaceAdmin = isAdminRoleName(
                        (me.role ?? '').trim().toLowerCase(),
                    );
                    this.notificationService.bootstrapFromSession();
                },
                error: () => {
                    /* keep defaults — e.g. session expired or network error */
                },
            });
        this.notificationService.bootstrapFromSession();
        void this.changelogService.refreshUnseenState();
        this.document.addEventListener('click', this.onDocumentClickCapture, true);
        this.destroyRef.onDestroy(() => {
            this.document.removeEventListener('click', this.onDocumentClickCapture, true);
        });
    }

    private readUserId(): string {
        try {
            return localStorage.getItem('id')?.trim() || '';
        } catch {
            return '';
        }
    }

    toggleDropdown(value: string) {
        if (this.activeDropdown.includes(value)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== value);
        } else {
            this.activeDropdown.push(value);
            // Same as Settings → Profile: fetch fresh /profile/me when opening the card.
            if (value === 'profile') {
                this.refreshHeaderProfile();
            }
        }
    }

    /** Match settings-profile reload: bust cache and push so other views stay in sync. */
    private refreshHeaderProfile(): void {
        this.auth
            .getProfileMe({ bustCache: true })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (me) => {
                    const s = mapProfileMeToSummary(me);
                    this.headerDisplayName = s.displayName;
                    this.headerUserEmail = s.email;
                    this.headerFirstName = (me.firstName ?? '').trim();
                    this.headerLastName = (me.lastName ?? '').trim();
                    this.headerAvatarUrl = s.photoUrl;
                    this.headerAvatarLoadFailed = false;
                    this.isWorkspaceAdmin = isAdminRoleName(
                        (me.role ?? '').trim().toLowerCase(),
                    );
                    this.auth.pushProfileMe(me);
                },
                error: () => {
                    /* keep current header values */
                },
            });
    }

    setTheme(t: 'light' | 'dark' | 'system'): void {
        this.storeData.dispatch({ type: 'toggleTheme', payload: t });
        const userId = this.readUserId();
        if (!userId) {
            return;
        }
        const colorScheme = t === 'dark' ? 'DARK' : t === 'system' ? 'SYSTEM' : 'LIGHT';
        this.membersApi.patchMemberByUserId(userId, { colorScheme }).subscribe({ error: () => {} });
    }

    signOut(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.auth.logout();
    }

    get showHeaderAvatarImage(): boolean {
        const url = this.headerAvatarUrl?.trim();
        return !!url && !isStaticProfileAvatarUrl(url) && !this.headerAvatarLoadFailed;
    }

    get headerUserInitials(): string {
        return initialsFromPersonName(
            this.headerFirstName,
            this.headerLastName,
            this.headerDisplayName,
        );
    }

    get notificationBadgeLabel(): string {
        const count = this.notificationService.unreadCount();
        return count > 99 ? '99+' : String(count);
    }

    onHeaderAvatarError(): void {
        this.headerAvatarLoadFailed = true;
    }

    openCommandPalette(event: Event): void {
        const target = event.currentTarget;
        this.paletteService.open(target instanceof HTMLElement ? target : null);
    }

    toggleNotificationPanel(): void {
        this.notificationPanelOpen = !this.notificationPanelOpen;
    }

    closeNotificationPanel(): void {
        this.notificationPanelOpen = false;
    }

    /** Capture phase so stopPropagation on filter/sort/date-picker toggles still dismisses the panel. */
    private readonly onDocumentClickCapture = (event: MouseEvent): void => {
        if (!this.notificationPanelOpen) {
            return;
        }
        const target = event.target as Node;
        if (
            this.notificationPanel?.nativeElement.contains(target) ||
            this.notificationBell?.nativeElement.contains(target)
        ) {
            return;
        }
        this.closeNotificationPanel();
    };
}
